import { Button, Col, Div, Image, Row, Text } from "atomize"
import { useContext, useState } from "react"
import { Link, Outlet, useLocation } from "react-router-dom"
import { motion, useReducedMotion } from "framer-motion"
import { theme } from "./settings/config"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBook, faUser, faUsers, faWandSparkles } from "@fortawesome/free-solid-svg-icons"
import DesktopLikeView from "./atoms/DesktopLikeView"
import SmartphoneView from "./atoms/SmartphoneView"
import SchoolSwitcher from "./school/SchoolSwitcher"
import PhoneHeader from "./header/PhoneHeader"
import { PhoneTitleProvider } from "./header/PhonePageTitle"
import { useShowPhoneCoachDropdown } from "./profile/PhoneCoachDropdown"
import { useTheme } from "./providers/theme.provider"
import SidebarSettings from "./components/SidebarSettings"
import {
	PHONE_ISLAND_SIDE,
	PHONE_ISLANDS_GAP,
	PHONE_NAV_BOTTOM,
	PHONE_NAV_HEIGHT,
} from "./atoms/phoneBars"
import { LessonsGlyph, NotebookGlyph, PlayersGlyph, ProfileFace } from "./atoms/NavIcons"
import { profileContext } from "./providers/profile.provider"
import { haptic } from "./utils/haptics.utils"

// Le voci della barra in basso sul telefono e di quella di lato sul computer.
// Il quaderno sta tra le lezioni e il profilo: è il lavoro di preparazione
// delle lezioni, non una cosa dell'account.
//
// Sul telefono il profilo sta a parte, ed è la propria foto; le altre icone
// sono quelle di src/atoms/NavIcons.js, le stesse ridisegnate a pezzi perché
// toccandole si muovono.
const NAV_ITEMS = [
	{ path: "/players", label: "Allievi", icon: faUsers, Glyph: PlayersGlyph },
	{ path: "/lessons", label: "Lezioni", icon: faWandSparkles, Glyph: LessonsGlyph },
	{ path: "/notebook", label: "Quaderno", icon: faBook, Glyph: NotebookGlyph },
	{ path: "/profile", label: "Profilo", icon: faUser },
]
const WORK_ITEMS = NAV_ITEMS.filter(({ path }) => path !== "/profile")

// la capsula arriva con la molla della fascia in cima (giorno / settimana),
// un po' più morbida: passa appena oltre la voce e torna
const CAPSULE_SLIDE = { type: "spring", stiffness: 420, damping: 30 }

const MobileNav = () => {
	const location = useLocation()
	const showCoachDropdown = useShowPhoneCoachDropdown()
	// Il lettering ha "Campo" in petrolio: al buio serve quello chiaro
	const { theme: currentTheme } = useTheme()
	const reduceMotion = useReducedMotion()
	// l'ultima voce toccata, e quante volte: la sua icona fa la sua mossa a
	// ogni tocco, anche se ci si è già dentro
	const [played, setPlayed] = useState({ path: null, count: 0 })
	const tap = path => {
		setPlayed(last => ({ path, count: last.count + 1 }))
		haptic("tick")
	}
	const { coach } = useContext(profileContext)
	const onProfile = location.pathname === "/profile"

	return (
		<Div m={{ b: "calc(0rem + env(safe-area-inset-bottom))" }} style={{ overflow: "hidden" }}>
			<SmartphoneView>
				{/* la pillola in cima e il titolo della pagina si parlano: lei dice
				    quanto è larga, la pagina che numeri metterci */}
				<PhoneTitleProvider>
					<PhoneHeader />
					{/* La barra in basso: due isole. A sinistra le tre voci del
					    lavoro (allievi, lezioni, quaderno), con la voce in cui si è
					    dentro in una capsula arancione chiaro; a destra, da solo, il
					    profilo, che è la propria foto o l'iniziale del nome. Prima
					    era una striscia attaccata al fondo, con icone grandi il
					    doppio, e il profilo stava in fila con le altre voci come se
					    fosse lavoro anche lui. Vedi src/atoms/phoneBars.js */}
					<Div
						pos="fixed"
						zIndex="2"
						d="flex"
						style={{
							left: PHONE_ISLAND_SIDE,
							right: PHONE_ISLAND_SIDE,
							bottom: PHONE_NAV_BOTTOM,
							height: PHONE_NAV_HEIGHT,
							gap: PHONE_ISLANDS_GAP,
						}}>
						<Div
							bg="surface"
							d="flex"
							p="0.375rem"
							shadow={theme.shadows.lift}
							transition
							style={{ flex: 1, borderRadius: "2rem", boxSizing: "border-box" }}>
							{WORK_ITEMS.map(({ path, label, Glyph }) => {
								const isActive = location.pathname === path
								const color = isActive ? theme.colors.primary : theme.colors.gunmetal
								return (
									<Link
										key={path}
										to={path}
										onClick={() => tap(path)}
										style={{
											position: "relative",
											flex: 1,
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											gap: "0.2rem",
											textDecoration: "none",
											WebkitTapHighlightColor: "transparent",
										}}>
										{/* una capsula sola, che scivola da una voce all'altra */}
										{isActive && (
											<motion.span
												layoutId="phone-nav-capsule"
												transition={reduceMotion ? { duration: 0.15 } : CAPSULE_SLIDE}
												style={{
													position: "absolute",
													inset: 0,
													// in px: mentre scivola framer-motion ricalcola gli
													// angoli, e i rem non li sa ricalcolare (verrebbe quadrata)
													borderRadius: "28px",
													background: theme.colors.primary_light,
												}}
											/>
										)}
										{/* il dito che preme schiaccia l'icona */}
										<motion.span
											whileTap={reduceMotion ? undefined : { scale: 0.82 }}
											style={{ position: "relative", display: "flex" }}>
											<Glyph color={color} play={played.path === path ? played.count : 0} />
										</motion.span>
										<Text
											textWeight={isActive ? "bold" : "500"}
											textColor={color}
											style={{ position: "relative", fontSize: "0.75rem", lineHeight: 1.1 }}>
											{label}
										</Text>
									</Link>
								)
							})}
						</Div>

						{/* il profilo: un'isola tonda alta quanto la barra, con la
						    foto dentro; dove si è dentro si accende */}
						<Link
							to="/profile"
							aria-label="Profilo"
							onClick={() => tap("/profile")}
							style={{
								width: PHONE_NAV_HEIGHT,
								flexShrink: 0,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								borderRadius: "50%",
								background: onProfile ? theme.colors.primary_light : theme.colors.surface,
								boxShadow: theme.shadows.lift,
								transition: "background 0.2s ease-in-out",
								WebkitTapHighlightColor: "transparent",
							}}>
							<motion.span
								whileTap={reduceMotion ? undefined : { scale: 0.85 }}
								style={{ display: "flex" }}>
								<ProfileFace
									coach={coach}
									size={50}
									active={onProfile}
									play={played.path === "/profile" ? played.count : 0}
								/>
							</motion.span>
						</Link>
					</Div>

					<Outlet />
					{/* il posto del maestro scelto, che sta sopra la barra in fondo:
					    nelle lezioni e nel profilo, non tra gli allievi né nel
					    quaderno, che sono gli stessi chiunque si guardi */}
					{showCoachDropdown && ["/lessons", "/profile"].includes(location.pathname) && (
						<Div h={"3rem"} pos="relative" />
					)}
				</PhoneTitleProvider>
			</SmartphoneView>
			<DesktopLikeView>
				<Row>
					<Div
						d="flex"
						justify="space-between"
						style={{ flexDirection: "column" }}
						align="center"
						bg="surface"
						p={{
							x: "1.5rem",
						}}
						rounded="20px"
						shadow={theme.shadows.accent}
						transition>
						<Image
							src={require(`./images/incampo_logo${
								currentTheme === "dark" ? "_dark" : ""
							}.png`)}
							w="8rem"
							m={{ t: "3rem" }}
						/>
						<Div>
							{NAV_ITEMS.map(({ path, label, icon }) => {
								const isActive = location.pathname === path
								return (
									<Link key={path} to={path}>
										<Button w="50%" bg="surface" h="fit-content" m="auto" p="0">
											<Div
												m={{ y: "0.5rem" }}
												p={{ x: "1rem", y: "0.5rem" }}
												d="flex"
												justify="space-between"
												rounded="circle"
												style={{ gap: "0.5rem" }}
												bg={isActive && theme.colors.primary_light}
												align="center"
												transition>
												<Div w="fit-content" m={{ x: "0.5rem" }} transition>
													<FontAwesomeIcon
														icon={icon}
														size="2x"
														color={isActive ? theme.colors.primary : theme.colors.gunmetal}
													/>
												</Div>
												<Text
													m={{ x: "0.5rem" }}
													textWeight={isActive && "bold"}
													textSize="subheader"
													textColor={"gunmetal"}>
													{label}
												</Text>
											</Div>
										</Button>
									</Link>
								)
							})}
						</Div>
						<SidebarSettings />
					</Div>
					<Col h="100vh" style={{ overflowY: "auto" }}>
						<SchoolSwitcher />
						<Outlet />
					</Col>
				</Row>
			</DesktopLikeView>
		</Div>
	)
}

export default MobileNav
